import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { setCurrentReportId } from "@/state/auth"
import { useEffect, useState } from "react"
import { Outlet } from "react-router-dom"
import { useParams, useNavigate } from "@/router"
import { Spinner } from "@/components/ui/spinner"
import {
	useDownloadReportForm,
	useDownloadTrips,
	useGetReport,
	useUpdateAndRegenerateReport,
} from "@/queries/useReport"
import { ReportContextProvider } from "./_context/ReportContext"
import { ReportNotFound } from "@/components/screens/report/overview/ReportNotFoundScreen"
import { defaultReportForm, reportFormDraftAtom, ReportSave } from "@/state/report"
import { OnboardingGeneratingScreen } from "@/components/screens/onboarding/GeneratingScreen"
import { useResetAtom } from "jotai/utils"
import axios from "axios"
import { z } from "zod"
import { tripsAtom } from "@/state/trips"

export default function ReportLayout() {
	const { id } = useParams("/report/:id")

	const setReportDraftForm = useSetAtom(reportFormDraftAtom)
	const report = useGetReport()
	const setTrips = useSetAtom(tripsAtom)
	const navigate = useNavigate()

	useEffect(() => {
		if (id) {
			setCurrentReportId(id)
		}
	}, [id])

	if (report.isLoading)
		return (
			<div className="h-screen w-screen flex items-center justify-center">
				<Spinner />
			</div>
		)

	if (!report.data?.form_url)
		return (
			<ReportNotFound
				onNewReport={() => {
					setCurrentReportId("")
					setTrips([])
					setReportDraftForm(defaultReportForm)
					navigate("/report/create/period")
				}}
			/>
		)

	return (
		<ReportDataLoader
			form_url={report.data.form_url}
			trips_url={report.data.trips_url}
			pdf_url={report.data.pdf_url}
			paid={report.data.paid}
			terms_accepted={report.data.terms_accepted}
		>
			<Outlet />
		</ReportDataLoader>
	)
}

export type ReportSaveWithLoadedForm = Omit<ReportSave, "form_url"> & { form_url: string }

type ReportDataLoaderProps = ReportSaveWithLoadedForm
const ReportDataLoader: React.FCC<ReportDataLoaderProps> = (props) => {
	// TODO: refactor to tesm
	const trips = useAtomValue(tripsAtom)
	const tripsQuery = useDownloadTrips(
		props.trips_url || "",
		!!props.trips_url && !trips.length
	)

	const { generating, run } = useUpdateAndRegenerateReport()

	const form = useDownloadReportForm(props.form_url, true)

	useEffect(() => {
		const f = async () => {
			try {
				if (tripsQuery.isLoading || form.isLoading || !form.data) return

				if (tripsQuery.data?.length || trips.length) return

				await run(form.data)
			} catch (e) {
			} finally {
			}
		}
		f()
	}, [form.isLoading, tripsQuery.isLoading])

	if (generating) return <OnboardingGeneratingScreen onNext={() => {}} />

	if (form.isLoading || tripsQuery.isLoading) {
		return (
			<div className="h-screen w-screen flex items-center justify-center">
				<Spinner />
			</div>
		)
	}

	return (
		<ReportContextProvider report={props} form={form.data!}>
			{props.children}
		</ReportContextProvider>
	)
}
